import { useQuery } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { useState } from 'react';
import { CopyId, ErrorState } from '@bb/ui';
import { ApiError, type AdminApi } from '../../../api/client.ts';
import { formatWhen } from '../../segments/SegmentsListPage.tsx';
import { formatSeconds } from './labels.ts';

const PAGE = 50;

/**
 * Entries waiting longer than their step should hold them (BB-123): a lost timer job or a broken event wait. The list
 * is live (not limited to the period), oldest first, and can be narrowed to one step. Profiles show their id to copy:
 * the dashboard has no profile page or message trace yet to link to.
 */
export function StuckEntriesTable({ environmentId, canvasId, steps, now = Date.now() }: { environmentId: string; canvasId: string; steps: Array<{ id: string; name: string }>; now?: number }) {
  const { api } = useRouter().options.context as { api: AdminApi };
  const [stepId, setStepId] = useState('');
  const [shown, setShown] = useState(PAGE);
  const stuck = useQuery({ queryKey: ['canvases', environmentId, 'stuck', canvasId, stepId], queryFn: () => api.canvasStuckEntries(environmentId, canvasId, stepId || undefined) });
  const nameOf = (id: string) => steps.find((s) => s.id === id)?.name ?? id;
  const entries = stuck.data?.entries ?? [];
  return (
    <section className="panel" aria-label="Stuck entries">
      <h2>Stuck entries</h2>
      <p className="hint">Entries still waiting an hour after their step should have let them go (a lost wake-up or a broken event wait). Live, whatever the period.</p>
      <label>
        Step{' '}
        <select
          value={stepId}
          onChange={(e) => {
            setStepId(e.target.value);
            setShown(PAGE);
          }}
        >
          <option value="">All steps</option>
          {steps.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
      </label>
      {stuck.isError ? (
        <ErrorState title="Stuck entries could not be loaded" message={stuck.error instanceof ApiError ? stuck.error.message : 'Something went wrong.'} onRetry={() => void stuck.refetch()} />
      ) : stuck.isPending ? (
        <p aria-busy="true">Loading stuck entries…</p>
      ) : entries.length === 0 ? (
        <p className="hint">Nothing is stuck.</p>
      ) : (
        <>
          <div className="push-table-scroll">
            <table className="table" aria-label="Stuck entries list">
              <thead>
                <tr>
                  <th scope="col">Profile</th>
                  <th scope="col">Step</th>
                  <th scope="col">Waiting since</th>
                  <th scope="col">Expected to leave by</th>
                  <th scope="col">Overdue</th>
                </tr>
              </thead>
              <tbody>
                {entries.slice(0, shown).map((e) => (
                  <tr key={e.entryId}>
                    <td>
                      <CopyId label={`profile id ${e.profileId}`} value={e.profileId} />
                    </td>
                    <td>{nameOf(e.stepId)}</td>
                    <td>{formatWhen(e.waitingSince)}</td>
                    <td>{formatWhen(e.expectedBy)}</td>
                    <td>{formatSeconds(Math.max(0, (now - Date.parse(e.expectedBy)) / 1000))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="hint">
            {entries.length > shown ? `Showing ${shown} of ${entries.length.toLocaleString('en-US')}. ` : `${entries.length.toLocaleString('en-US')} ${entries.length === 1 ? 'entry' : 'entries'}. `}
            {entries.length > shown && (
              <button type="button" className="link-button" onClick={() => setShown((v) => v + PAGE * 4)}>
                Show more
              </button>
            )}
          </p>
        </>
      )}
    </section>
  );
}
